import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton, InputAdornment } from '@mui/material';
import { useState } from 'react';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../shared/hooks/useAuth';
import Message from './Message';
import type { SignInPayload } from '../../types';
import Input from './Input';
import { buildYupLocale } from './buildYupLocale';
import InputWrapper from './InputWrapper';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const LoginForm = () => {
  const [result, setResult] = useState<null | string>(null);
  const [error, setError] = useState<null | string>(null);
  const [showPassword, setShowPassword] = useState<null | boolean>(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const { t } = useTranslation();
  buildYupLocale(t);

  const schema = yup
    .object({
      username: yup.string().required(),
      password: yup.string().required(),
    })
    .required();

  const auth = useAuth();

  const {
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<SignInPayload>({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<SignInPayload> = ({ username, password }) => {
    auth?.signin(
      { username, password },
      () => {
        setResult(t('login success'));
      },
      () => {
        setError(t('errors.login error'));
      },
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputWrapper>
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <Input
                errorFlag={Boolean(errors.username)}
                message={errors?.username?.message ?? ''}
                field={field}
                label={t('username')}
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                errorFlag={Boolean(errors.password)}
                message={errors?.password?.message ?? ''}
                field={field}
                label={t('password')}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('toggle password')}
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            )}
          />
        </InputWrapper>
        <InputWrapper>
          <Button type="submit" variant="contained">
            {t('login')}
          </Button>
        </InputWrapper>
        <Link to="/forgot">{t('forgot password')}</Link>
      </form>
      <InputWrapper>
        <Message error={error} message={result} />
      </InputWrapper>
    </>
  );
};

export default LoginForm;
